import { useCallback, useMemo, useState } from "react";
import { isKITProduct, type KITProductData, type ProductData } from "../../../../../../../models/ProductData";
import Parameter from "../../../../../../../common/components/Parameter";

import { type NumberFormatEvent } from "../../../../../../../common/components/CustomNumberFormat";
import { IconType } from "../../../../../../../common/components/icon/IconType";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCartThunk,
  removeProduct,
  updateCommentItem,
  updatePriceRef,
  updateProduct,
  updateQuantity,
} from "../../../../../../redux/actions";
import { fetchWishDetail } from "../../../../../../redux/thunks";
import {
  getSelectPriceParamsFromWishlistEntry,
  selectSubtotalForWishlistEntry,
} from "../../../../../../redux/wishlistSelectors";
import { sendWishlistProductClick } from "../../../../../../../feature/Analytics/ga/ga";
import { userIsCustomer, userIsRegistered } from "../../../../../../../utils/roles";
import { Permission, userHasAnyPermission } from "../../../../../../../utils/permissions";
import { canUseCommentsForProduct } from "../../../../../../../utils/productUtils";
import { type ResolvedWishlistEntryModel, type WishlistEntryModel } from "../../../../../../model";
import { Card } from "../../../../../../../Configurators/Linear/components/Card";
import {
  getSurchargeValueFromTotalPrice,
  getTariff,
  isProductPMA,
  selectNetPrice,
  selectTotalPrice,
} from "../../../../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import {
  getIsContractAvailable,
  isSpecialPrice,
} from "../../../../../../../ProductDetails/redux/productDetailsSelectors";
import sumBy from "lodash/sumBy";
import { ProductService } from "../../../../../../../AddToCart/ProductService";
import { text } from "../../../../../../../utils/PriceUtils";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  IconButton,
  InputLabel,
  styled,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import classes from "./WishlistItem.module.less";
import Checkbox from "~/common/components/Checkbox";
import ProductImage from "~/common/components/Product/ProductImage";
import { Translation } from "~/common/Translation";
import { PredefinedTraits } from "~/ProductDetails/components/ProductTrait";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/ProductComparison/ProductComparisonCheckbox";
import { WishlistMaterialIdInput } from "~/Wishlist/page/WishlistPage/components/WishlistDetails/components/WishlistItem/WishlistMaterialIdInput";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import { MAX_QUANTITY_FORMAT_LENGTH } from "~/utils/constants";
import { PriceReferenceParam } from "~/Cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/QuickEntryPopup/helpers";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type Row } from "@tanstack/react-table";
import { Icon } from "~/common/components/icon/Icon";
import { useIsMaxTabletSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Button } from "~/common/components/Button/Button";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { UndoButton } from "~/common/components/Button/UndoButton";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { LinkButton, LinkType } from "~/common/components/Link";
import classNames from "classnames";
import { isPriceReferenceParamVisible } from "~/Cart/components/CartPage/LineItemStaticParams";
import { obtainNetPriceLabels, obtainTotalPriceLabels } from "~/common/components/Price/v2/NetPrice";
import { type NetPriceData, type TotalPriceData, type WithSurcharge } from "~/models/PriceData";
import {
  IncludedPartsTable,
  OptionalPartsTable,
} from "~/Wishlist/page/WishlistPage/components/WishlistDetails/components/WishlistItem/PartTables";
import { useTranslation } from "react-i18next";
import { IncludedSurcharge } from "~/common/components/Product/ProductParams";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { productHasAvailableCampaign } from "~/SalesCampaign/utils";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { CommentButton } from "~/QuoteCart/components/ActiveQuoteEdit/ProductTable/CommentButton";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";

interface Props {
  index: number;
  row: Row<ResolvedWishlistEntryModel>;
  entryId: string;
  canAddToCart?: boolean;
}

const WIDTHS_MOBILE = {
  product: "124px",
  gap: "32px",
  brand: "30px",
  manufacturer: "27px",
};
const WIDTHS = {
  product: "100px",
  gap: "16px",
  brand: "30px",
  manufacturer: "27px",
};

export const WishlistItem = ({ index, canAddToCart, row }: Props) => {
  const { t } = useTranslation();
  const isMinTablet = useIsMinTabletSize();
  const isMaxTablet = useIsMaxTabletSize();

  const isTablet = isMinTablet && isMaxTablet;

  const wishlistEntry = row.original;
  const productDescriptor = wishlistEntry.product;
  const productData = productDescriptor.base;
  const isContractProduct = getIsContractAvailable(productDescriptor);

  const priceParams = getSelectPriceParamsFromWishlistEntry(wishlistEntry);
  const netPrice = useSelector(selectNetPrice(priceParams));
  const total = useSelector(selectTotalPrice(priceParams, priceParams.extraQuantity));

  const hasSales = productHasAvailableCampaign(productData.salesCampaign);

  const dispatch = useDispatch();
  const { onUpdateProduct, onUpdateQty, onUpdatePriceRef, onAddToCart, onUpdateCommentItem, onDeleteItem, onFetchPnA } =
    useMemo(
      () => ({
        onUpdateProduct: (newProduct: ProductData) => dispatch(updateProduct(row.original.id, newProduct)),
        onUpdateQty: (newQty?: number) => dispatch(updateQuantity(row.original.id, newQty)),
        onUpdatePriceRef: (newPriceRef?: string) => dispatch(updatePriceRef(row.original.id, newPriceRef!)),
        onUpdateCommentItem: (comment: string) => dispatch(updateCommentItem(row.original.id, comment)),
        onDeleteItem: () => dispatch(removeProduct(row.original.id)),
        onAddToCart: () => dispatch(addToCartThunk(row.original)),
        onFetchPnA: (code: string, priceReference: string) => dispatch(fetchWishDetail(code, priceReference)),
      }),
      [dispatch, row.original],
    );

  const isLoaded = productDescriptor.isLoaded;
  const isLoading = productDescriptor.isLoading;

  const updateQty = (e: NumberFormatEvent) => onUpdateQty(e.floatValue);

  let disableAddToCart;
  if (userIsCustomer()) {
    disableAddToCart = !canAddToCart || !isLoaded || wishlistEntry.quantity === undefined;
  } else {
    disableAddToCart = !canAddToCart || wishlistEntry.quantity === undefined;
  }
  disableAddToCart = disableAddToCart || window.SCHA.config.disableAllWS;

  const isRegistered = userIsRegistered();
  const canSeePrice = userHasAnyPermission(Permission.ADD_TO_QUOTE, Permission.ADD_TO_CART) && !isRegistered;

  const showLoadPnALink = canSeePrice && !isLoaded;

  const canSeeComment = canUseCommentsForProduct(productData.marketType);

  const visibleIndex = index + 1;

  const addToCartCb = useCallback(() => {
    sendWishlistProductClick(productData, "Select", visibleIndex);
    onAddToCart();
  }, [onAddToCart, productData, index]);

  const productDetailsCb = useCallback(() => {
    sendWishlistProductClick(productData, "Product information", visibleIndex);
  }, [productData, visibleIndex]);

  const isCustomer = userIsCustomer();

  const successorProduct = productDescriptor.base.successorProduct;
  const [previousProduct, setPreviousProduct] = useState<ProductData | null>(null);

  const isSpecial = isSpecialPrice(productDescriptor, "");

  const indexElement = <div className={classes.index}>{visibleIndex}</div>;

  const [commentRef, toggleComment] = useShowAndScrollInput(row.toggleExpanded);
  const hasComment = !!wishlistEntry.comment;
  const actions = (
    <div className={classes.actionContainer}>
      {canSeeComment && <CommentButton onClick={toggleComment} open={row.getIsExpanded()} fontSize={"small"} />}
      <IconButton color={"primary"} onClick={onDeleteItem} data-test-id={"wishlist-item-delete"}>
        <Icon className={classes.icon} size={16} type={IconType.Bin} />
      </IconButton>
      <Checkbox
        disabled={!row.getCanSelect()}
        checked={row.getIsSelected()}
        data-test-id={"wishlist-item-cbox-toggle"}
        onCheck={row.getToggleSelectedHandler()}
        className={classes.checkbox}
      />
    </div>
  );

  const image = (
    <ProductImage
      className={classes.image}
      product={productData}
      widths={isMinTablet ? WIDTHS : WIDTHS_MOBILE}
      onClick={productDetailsCb}
    />
  );

  const designationInput = (
    <div className={classes.designation}>
      <WishlistMaterialIdInput
        onChange={(p) => {
          onUpdateProduct(p);
          setPreviousProduct(null);
        }}
        value={productData.designation}
        isPreferred={productData.preferred}
        disabled={isKITProduct(productDescriptor)}
      />
    </div>
  );

  let descriptionBlock;
  if (isMinTablet) {
    descriptionBlock = (
      <div className={classes.description}>
        <div>
          <Translation {...productData.shortDescription} />
        </div>
        {!isRegistered && !isProductPMA(productDescriptor) && (
          <Parameter label={t("product.schaefflerId")} value={productData.sapMaterialNumber} />
        )}
        {isContractProduct && <PredefinedTraits.ContractProduct className={"mt-2"} />}
        {isProductComparisonCheckboxVisible(productDescriptor.base) && (
          <div className={classes.productComparison}>
            <ProductComparisonCheckbox productCode={productDescriptor.base.code} gaLocation={"Wishlist page"} />
          </div>
        )}
      </div>
    );
  } else {
    descriptionBlock = (
      <div className={classes.description}>
        <div>
          <Translation {...productData.shortDescription} />
        </div>
        {isContractProduct && <PredefinedTraits.ContractProduct className={"mt-2"} />}
        {isProductComparisonCheckboxVisible(productDescriptor.base) && (
          <div className={classes.productComparison}>
            <ProductComparisonCheckbox productCode={productDescriptor.base.code} gaLocation={"Wishlist page"} />
          </div>
        )}
      </div>
    );
  }

  const quantityInput = (
    <div className={classes.quantity}>
      {productData.quantityVisible ? (
        <CustomNumberFormat
          label={t("input.quantity")}
          data-test-id={"wishlist-item-input-quantity"}
          value={wishlistEntry.quantity}
          handleState={updateQty}
          maxLength={MAX_QUANTITY_FORMAT_LENGTH}
        />
      ) : (
        <Parameter label={t("input.quantity")} value={1} />
      )}
    </div>
  );

  const priceReferenceInput = isCustomer && (
    <PriceReferenceParam
      product={productData}
      className={classes.priceReference}
      priceReference={wishlistEntry.priceReference}
      onChange={onUpdatePriceRef}
      data-test-id={"package-entry-price-reference"}
      editable={canSeePriceRefColumn()}
    />
  );

  let priceBlock = null;
  if (!isLoaded && isLoading) {
    priceBlock = <CircleSpinner className={classes.priceBlock} />;
  }
  if (isLoaded) {
    const surchargeValue = getSurchargeValueFromTotalPrice(total);
    const hasSurcharge = netPrice?.surcharge ?? 0 > 0;
    const tariffSurcharge = isTariffSurchargeEnabled() && getTariff(row.original.product);

    priceBlock = (
      <div className={classes.priceBlock}>
        <NetPrice price={netPrice} hasSales={hasSales} />
        <div className={classes.verticalStack}>
          <TotalPrice total={total} tariffSurcharge={tariffSurcharge} />
          {hasSurcharge > 0 && (
            <IncludedSurcharge className={"mt-3"} value={surchargeValue} percent={netPrice?.surcharge ?? 0} />
          )}
          {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
        </div>
      </div>
    );
  }
  if (isLoaded && netPrice && netPrice.value <= 0 && !isProductPMA(productDescriptor)) {
    priceBlock = <div className={classes.priceBlock}>{t("product.priceOnRequest")}</div>;
  }

  let housingBlock = null;
  if (isKITProduct(productDescriptor)) {
    housingBlock = <KITSection product={productDescriptor} entry={wishlistEntry} />;
  }

  const cta = canAddToCart && (
    <Button
      data-test-id={"wishlist-item-add-to-cart"}
      size={"medium"}
      onClick={addToCartCb}
      className={classes.addToCart}
      startIcon={<Icon type={IconType.Cart} />}
      disabled={disableAddToCart}
    >
      {t("wishlistPage.button.add")}
    </Button>
  );

  const messagesBlock = (
    <div className={classes.messages}>
      {successorProduct && (
        <NewVersionMessage
          baseProduct={productDescriptor.base}
          onClick={() => {
            setPreviousProduct(productDescriptor.base);
            onUpdateProduct(successorProduct);
            ProductService.getProductDescriptor(successorProduct.code, wishlistEntry.priceReference, {
              fetch: true,
              forceFetchBasicData: true,
            });
          }}
          disableLink={true}
        />
      )}
      {previousProduct && (
        <UndoButton
          onClick={() => {
            setPreviousProduct(null);
            onUpdateProduct(previousProduct);
          }}
        />
      )}
      {!wishlistEntry.available && <Alert severity={"error"}>{t("wishlist.productUnavailable")}</Alert>}
      {showLoadPnALink && (
        <LinkButton
          variant={LinkType.PRIMARY}
          disabled={isLoading}
          data-test-id={"btn-load-pna"}
          onClick={(event) => {
            event.preventDefault();
            onFetchPnA(productData.code, wishlistEntry.priceReference);
          }}
          iconBefore={<Icon type={IconType.Reload} />}
        >
          {t("page.cart.table.loadPna")}
        </LinkButton>
      )}
      {isProductPMA(productDescriptor) && userIsCustomer() && <InfoProductMessagePMA />}
    </div>
  );

  let commentBlock = null;
  if (row.getIsExpanded()) {
    commentBlock = (
      <CommentInput
        label={t("wishlistPage.comment.label")}
        className={classes.commentBlock}
        value={wishlistEntry.comment}
        onChange={onUpdateCommentItem}
        ref={commentRef}
      />
    );
  }

  return (
    <Card
      className={classNames(classes.root, !isPriceReferenceParamVisible() && classes.noPriceRef)}
      disableHover={true}
    >
      <div className={classes.mobileHeaderSection}>
        {indexElement}
        {actions}
      </div>

      {hasSales && <SaleTag className={classes.saleTag} size={40} />}
      {image}
      {designationInput}
      {descriptionBlock}
      {priceReferenceInput}
      {quantityInput}
      {priceBlock}
      {!isTablet && (
        <>
          {housingBlock}
          {commentBlock}
          {messagesBlock}
          {cta}
        </>
      )}
      {isTablet && (
        <div className={classes.tabletBottomBlock}>
          {commentBlock}
          {housingBlock}
          {messagesBlock}
          {cta}
        </div>
      )}
    </Card>
  );
};

const KitParamContainer = styled("div")({
  display: "flex",
  paddingLeft: "30px",
  marginTop: "20px",
});

interface ParamProps {
  entry: WishlistEntryModel;
  product: ProductDescriptor<KITProductData>;
}

const useTotalQuantityParamCls = makeStyles({
  root: {
    paddingLeft: `${16}px`,
    marginLeft: `${300}px`,
    width: `${400}px`,
  },
});

const TotalQuantityParam = ({ entry, product }: ParamProps) => {
  const optionalPartsTotalBaseQty = sumBy(
    entry.optionalParts,
    (part) => product.base.optionalParts.find((partData) => partData.product.code === part.productCode)?.quantity ?? 0,
  );
  const includedPartsTotalBaseQty = sumBy(product.base.includedParts, (part) => part.quantity);
  const totalQty = entry.quantity * (optionalPartsTotalBaseQty + includedPartsTotalBaseQty);

  const { root } = useTotalQuantityParamCls();

  const { t } = useTranslation();

  return (
    <Parameter className={root} label={t("cartPage.table.item")}>
      {totalQty}
    </Parameter>
  );
};

const useSubtotalParamCls = makeStyles({
  root: {
    paddingLeft: `${16}px`,
    marginLeft: `${200}px`,
  },
});

const SubtotalParam = ({ entry, product }: ParamProps) => {
  ProductService.useMultipleProductDescriptors({
    productCodes: entry.optionalParts.map((_) => _.productCode).concat(entry.productCode),
    priceReference: entry.priceReference,
    options: { fetch: true },
  });

  const subtotal = useSelector(selectSubtotalForWishlistEntry(entry.id));

  const { root } = useSubtotalParamCls();

  const { t } = useTranslation();

  return <Parameter className={root} label={t("page.wishlist.housingSubtotal")} value={text(subtotal)} />;
};

const NetPrice = ({ price, hasSales }: { price: NetPriceData | null | undefined; hasSales: boolean }) => {
  const { t } = useTranslation();
  const labels = obtainNetPriceLabels(price, t);

  return (
    <FormControl>
      <InputLabel className={classes.priceLabel} shrink={true}>
        {labels.fullLabel}
      </InputLabel>
      <div className={classNames("MuiInput-formControl", classes.priceElement)}>{labels.price}</div>
      <div>{!hasSales && labels.extraLabels.map(({ full }, i) => <div key={i}>{full}</div>)}</div>
    </FormControl>
  );
};

const TotalPrice = ({
  total,
  tariffSurcharge,
}: {
  total: (TotalPriceData & WithSurcharge) | null | undefined;
  tariffSurcharge: boolean;
}) => {
  const { t } = useTranslation();
  const labels = obtainTotalPriceLabels(total, t);

  return (
    <FormControl classes={{ root: "f-bold" }}>
      <InputLabel className={classes.priceLabel} shrink={true}>
        {labels.fullLabel}
      </InputLabel>
      <div className={classNames("MuiInput-formControl", classes.priceElement)}>{labels.total}</div>
    </FormControl>
  );
};

interface KITSectionProps {
  className?: string;
  product: ProductDescriptor;
  entry: ResolvedWishlistEntryModel;
}
const KITSection = ({ className, product, entry }: KITSectionProps) => {
  const { t } = useTranslation();
  const tableProps = {
    product: product.base as KITProductData,
    quantity: entry.quantity,
    priceReference: entry.priceReference,
  };
  return (
    <Accordion classes={{ root: classes.kitSectionRoot }}>
      <AccordionSummary classes={{ root: classes.kitSectionSummary, content: classes.kitSectionSummaryContent }}>
        {t("page.wishlist.section.housing.title")}
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.kitSectionDetails }}>
        <IncludedPartsTable {...tableProps} />
        <OptionalPartsTable wishlistOptionalParts={entry.optionalParts} {...tableProps} />
      </AccordionDetails>
    </Accordion>
  );
};
